import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { X, Package2, ChevronDown, AlertTriangle, ShoppingCart, Check } from 'lucide-react';
import { StockItem } from '../lib/stockItems';
import { getSuppliers, Customer } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import { useBusiness } from '../contexts/BusinessContext';
import OrderSummaryPDFModal from './OrderSummaryPDFModal';
import CreateOrderModal from './CreateOrderModal';
import {
  createOrder,
  OrderItem as DBOrderItem,
  getPendingOrdersForItems,
  updateOrderEmailInfo,
  linkEmailToOrder,
} from '../lib/purchaseOrders';
import { toast } from '../components/toasts/SimpleToast';

interface PlaceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  items: StockItem[];
}

interface OrderItem {
  item: StockItem;
  quantityToOrder: number;
  totalCost: number;
}

interface SupplierOrder {
  supplier: Customer;
  items: OrderItem[];
  totalCost: number;
  itemCount: number;
}

export default function PlaceOrderModal({
  isOpen,
  onClose,
  items
}: PlaceOrderModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  // Auto-elevate above a parent modal (e.g. the Messages create menu); standalone
  // keeps the original z-index.
  const resolvedZIndex = useModalZIndex(100010);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<Customer[]>([]);
  const [supplierOrders, setSupplierOrders] = useState<SupplierOrder[]>([]);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<{
    supplierId: string;
    supplier: Customer;
    items: OrderItem[];
    totalAmount: number;
  } | null>(null);
  const [pendingOrderItems, setPendingOrderItems] = useState<Map<string, number>>(new Map());
  const [pendingOrderNumbers, setPendingOrderNumbers] = useState<Map<string, string[]>>(new Map());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  // Ref so onEmailSent (same tick as create) always sees the new PO id.
  const lastCreatedOrderIdRef = useRef<string | null>(null);
  
  // Load suppliers
  const loadSuppliers = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoading(true);
      const data = await getSuppliers(selectedBusiness.id);
      setSuppliers(data);
      setError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.id]);
  
  // Load pending orders
  const loadPendingOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      const pendingItems = await getPendingOrdersForItems(selectedBusiness.id);
      
      // Convert to Maps for easy lookup
      const itemMap = new Map<string, number>();
      const orderNumbersMap = new Map<string, string[]>();
      pendingItems.forEach(item => {
        itemMap.set(item.itemId, item.quantityOrdered);
        orderNumbersMap.set(item.itemId, item.orderNumbers || []);
      });
      
      setPendingOrderItems(itemMap);
      setPendingOrderNumbers(orderNumbersMap);
      
    } catch (err) {
      console.error('Error loading pending orders:', err);
      // Don't set error here to avoid blocking the UI
    }
  }, [selectedBusiness?.id]);
  
  // Calculate items that need to be ordered (below reorder point)
  const calculateOrderItems = useCallback(() => {
    console.log('🔍 PlaceOrderModal: calculateOrderItems called');
    console.log('🔍 Total items received:', items.length);
    
    // Filter items that are below reorder point and have a supplier
    // Only include items where is_one_time = false and is_deleted = false
    // Show ALL items below threshold, even if they have pending orders
    const itemsToOrder = items.filter(item => {
      // First check if item is deleted or one-time
      if (item.is_deleted) {
        console.log(`  ❌ ${item.name}: filtered out (is_deleted)`);
        return false;
      }
      if (item.is_one_time) {
        console.log(`  ❌ ${item.name}: filtered out (is_one_time)`);
        return false;
      }
      
      // Check basic conditions
      const reorderPoint = item.reorder_point ?? 0;
      const maxQuantity = item.max_quantity ?? 0;
      const currentQuantity = item.quantity ?? 0;
      
      console.log(`  📦 ${item.name}: qty=${currentQuantity}, reorder=${reorderPoint}, max=${maxQuantity}, supplier=${!!item.supplier_id}`);
      
      // Items with reorder_point of 0 should NOT be included in quick order section
      // Only items with reorder_point > 0 should appear here
      if (reorderPoint === 0 || reorderPoint === null) {
        console.log(`  ❌ ${item.name}: filtered out (reorder_point is 0 or null)`);
        return false;
      }
      
      // Item is low stock if current quantity is at or below reorder point
      const isLowStock = currentQuantity <= reorderPoint;
      const hasSupplier = !!item.supplier_id;
      // Can add more stock if max_quantity exists and is > current quantity
      const canAddMoreStock = maxQuantity > 0 && maxQuantity > currentQuantity;
      
      console.log(`  ${isLowStock ? '✅' : '❌'} ${item.name}: isLowStock=${isLowStock}, hasSupplier=${hasSupplier}, canAddMoreStock=${canAddMoreStock}`);
      
      const shouldInclude = isLowStock && hasSupplier && canAddMoreStock;
      if (!shouldInclude) {
        console.log(`  ❌ ${item.name}: filtered out (isLowStock=${isLowStock}, hasSupplier=${hasSupplier}, canAddMoreStock=${canAddMoreStock})`);
      }
      
      return shouldInclude;
    });
    
    console.log('✅ Items that passed filter:', itemsToOrder.length);
    itemsToOrder.forEach(item => {
      console.log(`  ✅ ${item.name}: qty=${item.quantity}, reorder=${item.reorder_point}, max=${item.max_quantity}`);
    });
    
    // Group by supplier
    const ordersBySupplier: Record<string, SupplierOrder> = {};
    
    itemsToOrder.forEach(item => {
      if (!item.supplier_id) return; // Skip items without supplier
      
      const supplier = suppliers.find(s => s.id === item.supplier_id);
      if (!supplier) return; // Skip if supplier not found
      
      // Get pending order quantity
      const pendingQuantity = pendingOrderItems.get(item.id) || 0;
      const currentQuantity = item.quantity || 0;
      const projectedQuantity = currentQuantity + pendingQuantity;
      const reorderPoint = item.reorder_point || 0;
      const maxQuantity = item.max_quantity || 0;
      
      // Calculate how much quantity to order considering pending orders
      // Order if projected quantity is at or below reorder point (bring up to max quantity)
      // Note: Items with reorder_point === 0 are filtered out above
      let quantityToOrder = 0;
      if (projectedQuantity <= reorderPoint) {
        // Still need to order: bring up to max quantity
        quantityToOrder = maxQuantity - projectedQuantity;
        console.log(`  📊 ${item.name}: projectedQty=${projectedQuantity}, reorderPoint=${reorderPoint}, quantityToOrder=${quantityToOrder}`);
      } else {
        console.log(`  ⚠️ ${item.name}: projectedQty=${projectedQuantity} > reorderPoint=${reorderPoint}, no order needed`);
      }
      
      // Always include the item in the list (even if quantityToOrder is 0)
      // so we can show "on order" status if it has pending orders
      const totalCost = quantityToOrder * item.acquisition_cost;
      
      if (!ordersBySupplier[supplier.id]) {
        ordersBySupplier[supplier.id] = {
          supplier,
          items: [],
          totalCost: 0,
          itemCount: 0
        };
      }
      
      ordersBySupplier[supplier.id].items.push({
        item,
        quantityToOrder,
        totalCost
      });
      
      // Only add to total cost and count if we actually need to order
      if (quantityToOrder > 0) {
        ordersBySupplier[supplier.id].totalCost += totalCost;
        ordersBySupplier[supplier.id].itemCount += 1;
      }
    });
    
    // Convert to array and sort by supplier name
    const ordersArray = Object.values(ordersBySupplier)
      .sort((a, b) => {
        const aName = `${a.supplier.first_name} ${a.supplier.last_name}`.trim();
        const bName = `${b.supplier.first_name} ${b.supplier.last_name}`.trim();
        return aName.localeCompare(bName);
      });
    
    console.log('📋 Final supplier orders:', ordersArray.length, 'suppliers');
    ordersArray.forEach(order => {
      console.log(`  📦 ${order.supplier.first_name} ${order.supplier.last_name}: ${order.items.length} items, ${order.items.filter(i => i.quantityToOrder > 0).length} need ordering`);
    });
    
    setSupplierOrders(ordersArray);
  }, [items, suppliers, pendingOrderItems]);
  
  // Initialize selectedItems when supplier orders are calculated
  // Only auto-select items where we actually need to order (quantityToOrder > 0)
  useEffect(() => {
    if (supplierOrders.length > 0) {
      const itemsToSelect = new Set<string>();
      supplierOrders.forEach(order => {
        order.items.forEach(({ item, quantityToOrder }) => {
          // Only auto-select if we actually need to order (quantityToOrder > 0)
          if (quantityToOrder > 0) {
            itemsToSelect.add(item.id);
          }
        });
      });
      // Always update selectedItems when supplierOrders change, not just when it's empty
      // This ensures correct selection when modal reopens
      setSelectedItems(itemsToSelect);
    }
  }, [supplierOrders]);
  
  // Toggle supplier expansion
  const toggleSupplier = (supplierId: string) => {
    setExpandedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) {
        next.delete(supplierId);
      } else {
        next.add(supplierId);
      }
      return next;
    });
  };
  
  // Update order quantity for an item
  const updateOrderQuantity = (supplierId: string, itemId: string, quantity: number) => {
    setSupplierOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.supplier.id !== supplierId) return order;
        
        const updatedItems = order.items.map(orderItem => {
          if (orderItem.item.id !== itemId) return orderItem;
          
          const newQuantity = Math.max(1, quantity); // Ensure at least 1
          const newTotalCost = newQuantity * orderItem.item.acquisition_cost;
          
          return {
            ...orderItem,
            quantityToOrder: newQuantity,
            totalCost: newTotalCost
          };
        });
        
        // Calculate new total cost
        const newTotalCost = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
        
        return {
          ...order,
          items: updatedItems,
          totalCost: newTotalCost
        };
      });
    });
  };
  
  // Handle placing an order
  const handlePlaceOrder = (supplierId: string) => {
    const orderGroup = supplierOrders.find(o => o.supplier.id === supplierId);
    if (!orderGroup) {
      console.error('Order group not found for supplier', supplierId);
      return;
    }
    
    // Filter items to only include selected ones
    const selectedItemsForOrder = orderGroup.items.filter(({ item }) => 
      selectedItems.has(item.id)
    );
    
    if (selectedItemsForOrder.length === 0) {
      toast.error({ title: 'No Items Selected', description: 'Please select at least one item to order.' });
      return;
    }
    
    const selectedTotalAmount = selectedItemsForOrder.reduce((sum, { totalCost }) => sum + totalCost, 0);
    
    setSelectedOrder({
      supplierId,
      supplier: orderGroup.supplier,
      items: selectedItemsForOrder,
      totalAmount: selectedTotalAmount
    });
  };
  
  // Function to handle sending the order
  const handleSendOrder = async (orderNumber: string) => {
    if (!selectedOrder) return false;
    
    try {
      setLoading(true);
      
      // Use the orderNumber passed from OrderSummaryPDFModal (ensures email and DB match)
      
      if (!selectedBusiness?.id) {
        throw new Error('No business selected');
      }
      
      // Prepare order items for the database
      const dbOrderItems: DBOrderItem[] = selectedOrder.items.map(item => ({
        item_id: item.item.id,
        quantity: item.quantityToOrder,
        unit_price: item.item.acquisition_cost,
        total_price: item.totalCost,
        item_name: item.item.name,
        item_unit: item.item.unit
      }));
      
      // Create the order in the database (the PO system assigns the real number)
      const created = await createOrder(
        {
          business_id: selectedBusiness.id,
          supplier_id: selectedOrder.supplier.id,
          order_number: orderNumber,
          total_amount: selectedOrder.totalAmount,
          status: 'pending'
        },
        dbOrderItems
      );
      if (created.id) lastCreatedOrderIdRef.current = created.id;
      const assignedNumber = created.order_number || orderNumber;
      
      // Show success notification
      toast.success({
        title: "Order Placed",
        description: `Order ${assignedNumber} has been created and sent to ${selectedOrder.supplier.first_name} ${selectedOrder.supplier.last_name}.`,
        duration: 5000,
      });
      
      // Refresh pending orders
      await loadPendingOrders();
      
      // After the order is successfully sent, update the supplier orders
      // by removing the supplier that was just ordered from
      setSupplierOrders(prevOrders => 
        prevOrders.filter(order => order.supplier.id !== selectedOrder.supplierId)
      );
      
      // Clear any expanded state for this supplier
      setExpandedSuppliers(prev => {
        const next = new Set(prev);
        next.delete(selectedOrder.supplierId);
        return next;
      });
      
      // Note: selectedItems will be reset when modal opens,
      // so items will show up again next time if they still need to be ordered
      
      // Return the DB-assigned PO number so the email/SMS use it.
      return assignedNumber;
    } catch (err) {
      console.error('Error sending order:', err);
      setError(err instanceof Error ? err.message : 'Failed to send order');
      
      // Show error notification
      toast.error({
        title: "Order Failed",
        description: err instanceof Error ? err.message : 'Failed to send order',
        duration: 5000,
      });
      
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Close the order summary modal
  const handleCloseOrderSummary = () => {
    setSelectedOrder(null);
  };
  
  // Initialize when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Reset selectedItems when modal opens so it can be properly initialized
      setSelectedItems(new Set());
      loadSuppliers();
      loadPendingOrders();
    }
  }, [isOpen, loadSuppliers, loadPendingOrders]);
  
  // Calculate orders after suppliers and pending orders are loaded
  useEffect(() => {
    if (suppliers.length > 0) {
      calculateOrderItems();
    }
  }, [suppliers, pendingOrderItems, calculateOrderItems]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };


  // Handle toggling item selection
  const handleToggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const updated = new Set(prev);
      if (updated.has(itemId)) {
        updated.delete(itemId);
      } else {
        updated.add(itemId);
      }
      return updated;
    });
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 transition-opacity duration-200 ${
          isOpen ? 'bg-black/50' : 'bg-black/0'
        }`}
        style={{
          zIndex: resolvedZIndex,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
        }}
        onClick={onClose}
        onTransitionEnd={handleTransitionEnd}
      >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: resolvedZIndex + 40
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Place Order
            </h2>
          </div>

          {/* Create Order Button */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <button
              onClick={() => setIsCreateOrderModalOpen(true)}
              className="w-full p-3.5 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 active:bg-blue-800 transition-all duration-150 active:scale-[0.98] flex items-center justify-center gap-2 shadow-sm font-semibold"
              style={{ WebkitTapHighlightColor: 'transparent', fontSize: '15px' }}
            >
              <ShoppingCart className="h-5 w-5" />
              Create Order
            </button>
          </div>

          {/* Content Section */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600">Loading orders...</p>
                  </div>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <p className="mb-3">{error}</p>
                  <button 
                    onClick={loadSuppliers}
                    className="px-4 py-2 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 active:scale-[0.98] rounded-xl transition-all duration-150 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    Try again
                  </button>
                </div>
              ) : supplierOrders.length === 0 ? (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                  <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">No items to order</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    All items are above their reorder point.
                  </p>
                  
                  {/* Show items skipped because of pending orders */}
                  {pendingOrderItems.size > 0 && (
                    <div className="mt-6 text-left bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">Items already in pending orders</h4>
                      <p className="text-xs text-blue-700 mb-3">
                        The following items have pending orders and don't need to be reordered at this time.
                      </p>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {items
                          .filter(item => !item.is_deleted && pendingOrderItems.has(item.id) && item.reorder_point !== null && item.quantity <= item.reorder_point)
                          .map(item => (
                            <div key={item.id} className="flex justify-between text-xs text-gray-700 p-2 bg-white rounded-xl border border-blue-100">
                              <span className="truncate pr-2">{item.name}</span>
                              <span className="font-semibold whitespace-nowrap">{pendingOrderItems.get(item.id) || 0} {item.unit}(s) ordered</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  
                  {supplierOrders.map((order) => (
                    <div 
                      key={order.supplier.id} 
                      className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden"
                    >
                      <div 
                        className="bg-gray-50 p-4 cursor-pointer border-b border-gray-200 active:bg-gray-100 transition-colors"
                        onClick={() => toggleSupplier(order.supplier.id)}
                        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center flex-1 min-w-0">
                            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-gray-700 flex items-center justify-center text-xs sm:text-sm font-bold text-white mr-2 sm:mr-3 flex-shrink-0">
                              {getPersonInitials(order.supplier.first_name, order.supplier.last_name)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                                {order.supplier.first_name} {order.supplier.last_name}
                              </h3>
                              {order.supplier.email && (
                                <p className="text-xs text-gray-500 truncate mt-0.5">
                                  {order.supplier.email}
                                </p>
                              )}
                              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 sm:gap-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] sm:text-xs font-medium bg-red-100 text-red-800 whitespace-nowrap">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Low Stock
                                </span>
                                <span className="text-[10px] sm:text-xs text-gray-500 whitespace-nowrap">
                                  {(() => {
                                    const selectedItemsForSupplier = order.items.filter(({ item }) => selectedItems.has(item.id));
                                    const selectedCount = selectedItemsForSupplier.length;
                                    const selectedTotal = selectedItemsForSupplier.reduce((sum, { totalCost }) => sum + totalCost, 0);
                                    return `${selectedCount} item${selectedCount !== 1 ? 's' : ''} • $${selectedTotal.toFixed(2)}`;
                                  })()}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handlePlaceOrder(order.supplier.id);
                              }}
                              className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation whitespace-nowrap"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <ShoppingCart className="h-3.5 w-3.5 inline-block mr-1.5" />
                              <span className="hidden sm:inline">Quick Order</span>
                              <span className="sm:hidden">Quick</span>
                            </button>
                            <ChevronDown
                              className={`h-5 w-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                                expandedSuppliers.has(order.supplier.id) ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                      
                      {expandedSuppliers.has(order.supplier.id) && (
                        <div className="divide-y divide-gray-200">
                          {order.items.map(({ item, quantityToOrder, totalCost }) => {
                            const isSelected = selectedItems.has(item.id);
                            const onOrderQuantity = pendingOrderItems.get(item.id) || 0;
                            const orderNumbers = pendingOrderNumbers.get(item.id) || [];
                            // Only consider it a pending order if we have actual order numbers
                            const hasPendingOrder = onOrderQuantity > 0 && orderNumbers.length > 0;
                            return (
                            <div key={item.id} className={`p-4 ${!isSelected && quantityToOrder > 0 ? 'opacity-60' : ''}`}>
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900 flex-1">{item.name}</h4>
                                  </div>
                                  {item.description && (
                                    <p className="mt-1 text-xs text-gray-600 line-clamp-2">{item.description}</p>
                                  )}
                                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                    <span className="font-semibold text-red-600 bg-red-50 px-2 py-1 rounded-xl whitespace-nowrap">
                                      Current: {item.quantity} {item.unit}
                                    </span>
                                    {hasPendingOrder && orderNumbers.length > 0 && (
                                      <>
                                        <span className="font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-xl whitespace-nowrap">
                                          On Order: {onOrderQuantity} {item.unit}
                                        </span>
                                        <span className="font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded-xl whitespace-nowrap">
                                          PO: {orderNumbers.join(', ')}
                                        </span>
                                      </>
                                    )}
                                    <span className="font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded-xl whitespace-nowrap">
                                      Max: {item.max_quantity} {item.unit}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex sm:flex-col sm:text-right sm:ml-4 gap-3 sm:gap-0 items-center sm:items-end">
                                  {quantityToOrder > 0 ? (
                                    <>
                                      <div className="flex-1 sm:flex-none">
                                        <p className="text-base font-semibold text-gray-900">${totalCost.toFixed(2)}</p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          @ ${item.acquisition_cost.toFixed(2)}/{item.unit}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="number"
                                          min="1"
                                          value={quantityToOrder}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => 
                                            updateOrderQuantity(
                                              order.supplier.id,
                                              item.id,
                                              parseInt(e.target.value) || 1
                                            )
                                          }
                                          className="w-20 px-3 py-2 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                          style={{ fontSize: '16px' }}
                                        />
                                        <span className="text-xs text-gray-500 font-semibold whitespace-nowrap">{item.unit}(s)</span>
                                      </div>
                                    </>
                                  ) : hasPendingOrder && orderNumbers.length > 0 ? (
                                    <div className="text-xs text-gray-500 font-medium">
                                      On order - no additional needed
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-500 font-medium">
                                      No order needed
                                    </div>
                                  )}
                                </div>
                                {/* Only show checkbox if item needs to be ordered (quantityToOrder > 0) */}
                                {quantityToOrder > 0 && (
                                  <div className="flex items-center justify-end sm:justify-start">
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleToggleItemSelection(item.id);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="sr-only"
                                      />
                                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-150 ${
                                        isSelected
                                          ? 'bg-blue-600 border-blue-600'
                                          : 'bg-white border-gray-300 hover:border-blue-400'
                                      }`}>
                                        {isSelected && (
                                          <Check className="h-4 w-4 text-white" />
                                        )}
                                      </div>
                                    </label>
                                  </div>
                                )}
                              </div>
                            </div>
                          )})}
                          <div className="bg-gray-50 p-4 border-t border-gray-200">
                            <div className="flex justify-between items-center mb-3">
                              <p className="text-sm font-semibold text-gray-700">Order Total</p>
                              <p className="text-lg font-semibold text-blue-600">
                                ${order.items
                                  .filter(({ item }) => selectedItems.has(item.id))
                                  .reduce((sum, { totalCost }) => sum + totalCost, 0)
                                  .toFixed(2)}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlaceOrder(order.supplier.id);
                              }}
                              className="w-full py-3 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 active:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-150 active:scale-[0.98] touch-manipulation md:hidden"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <ShoppingCart className="h-4 w-4 inline-block mr-2" />
                              Quick Order
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
      
      {/* Render the OrderSummaryPDFModal when an order is selected */}
      {selectedOrder && (
        <OrderSummaryPDFModal
          isOpen={!!selectedOrder}
          onClose={handleCloseOrderSummary}
          supplier={selectedOrder.supplier}
          items={selectedOrder.items}
          totalAmount={selectedOrder.totalAmount}
          onSendOrder={handleSendOrder}
          onEmailSent={async (orderNumber, messageId, threadId, emailBody) => {
            const poId = lastCreatedOrderIdRef.current;
            if (!selectedBusiness?.id || !poId || !selectedOrder.supplier?.email) return;
            try {
              await updateOrderEmailInfo(poId, messageId, threadId);
              await linkEmailToOrder(
                poId,
                selectedBusiness.id,
                selectedOrder.supplier.id,
                selectedOrder.supplier.email,
                `[ORDER-${orderNumber}] ${orderNumber}`,
                emailBody,
                messageId
              );
            } catch (err) {
              console.error('Error linking Place Order email to PO:', err);
            }
          }}
        />
      )}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return (
    <>
      {createPortal(modalContent, document.body)}
      <CreateOrderModal
        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        items={items}
      />
    </>
  );
} 